import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { forwardRef } from 'react';
import { cn } from '../lib/cn.js';
/**
 * Dialog primitives.
 *
 * Radix is used rather than a hand-rolled modal because a correct dialog is
 * mostly invisible work: trapping focus inside it, restoring focus to the
 * control that opened it, marking the rest of the page inert for screen
 * readers, closing on Escape, and labelling itself by its own title. Each of
 * those is a WCAG requirement and each is easy to omit without noticing —
 * a hand-built modal usually looks perfect and traps a keyboard user behind
 * the page it covers.
 */
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogContent = forwardRef(function DialogContent({ className, children, hideClose = false, ...props }, ref) {
    return (_jsxs(DialogPrimitive.Portal, { children: [_jsx(DialogPrimitive.Overlay, { className: "fixed inset-0 z-overlay bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out" }), _jsxs(DialogPrimitive.Content, { ref: ref, className: cn('fixed left-1/2 top-1/2 z-modal grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-4 rounded-lg border bg-background p-6 shadow-lg', className), ...props, children: [children, hideClose ? null : (_jsx(DialogPrimitive.Close, { className: "absolute right-4 top-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", "aria-label": "Close", children: _jsx(X, { className: "h-4 w-4", "aria-hidden": "true" }) }))] })] }));
});
export function DialogHeader({ className, ...props }) {
    return _jsx("div", { className: cn('flex flex-col gap-1.5', className), ...props });
}
export function DialogFooter({ className, ...props }) {
    return (_jsx("div", { className: cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className), ...props }));
}
export const DialogTitle = forwardRef(function DialogTitle({ className, ...props }, ref) {
    return (_jsx(DialogPrimitive.Title, { ref: ref, className: cn('text-lg font-semibold leading-none', className), ...props }));
});
export const DialogDescription = forwardRef(function DialogDescription({ className, ...props }, ref) {
    return (_jsx(DialogPrimitive.Description, { ref: ref, className: cn('text-sm text-muted-foreground', className), ...props }));
});
//# sourceMappingURL=dialog.js.map